"use client";

import { useEffect, useRef, type FC } from "react";
import type { RenderConversation } from "./types";
import { MessageRenderer } from "./MessageRenderer";

export type ViewPreset = "desktop" | "mobile" | "responsive";

export const VIEW_CONFIG: Record<
  ViewPreset,
  { width: string; maxWidth?: string; padding: string }
> = {
  desktop: { width: "860px", padding: "1rem" },
  mobile: { width: "390px", padding: "0.75rem" },
  responsive: { width: "100%", maxWidth: "860px", padding: "1rem" },
};

type ConversationRendererProps = {
  conversation: RenderConversation;
  view?: ViewPreset;
  /**
   * Whether to set window.__RENDER_READY__ after fonts/images load.
   * Set to true for Playwright screenshot routes, false for embeds.
   * @default true
   */
  signalReady?: boolean;
};

declare global {
  interface Window {
    __RENDER_READY__?: boolean;
  }
}

export const ConversationRenderer: FC<ConversationRendererProps> = ({
  conversation,
  view = "desktop",
  signalReady = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Skip ready signaling if disabled (for embeds)
    if (!signalReady) return;

    const container = containerRef.current;
    if (!container) return;

    // Wait for fonts and images to load before signaling ready
    const waitForReady = async () => {
      // Wait for all fonts to load
      await document.fonts.ready;

      // Wait for all images in the container to load
      const images = Array.from(container.querySelectorAll("img"));
      const incompleteImages = images.filter((img) => !img.complete);

      if (incompleteImages.length > 0) {
        await Promise.all(
          incompleteImages.map(
            (img) =>
              new Promise<void>((resolve) => {
                img.onload = () => resolve();
                img.onerror = () => resolve();
              }),
          ),
        );
      }

      // Signal that rendering is complete
      window.__RENDER_READY__ = true;
    };

    waitForReady();
  }, [signalReady]);

  const { messages } = conversation;
  const viewConfig = VIEW_CONFIG[view];

  return (
    <div
      ref={containerRef}
      className="conversation-renderer-container"
      style={{
        width: viewConfig.width,
        maxWidth: viewConfig.maxWidth,
        paddingLeft: viewConfig.padding,
        paddingRight: viewConfig.padding,
        margin: "0 auto",
      }}
    >
      <div className="flex-1">
        {messages.map((message, index) => (
          <MessageRenderer key={index} message={message} view={view} />
        ))}
      </div>
    </div>
  );
};
